import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  XCircle,
  Calendar,
  User,
  Phone,
  Mail,
  MapPin,
  Home,
  DollarSign,
  Image as ImageIcon,
  Shield,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../../services/api';

const ServiceReportManagement
 = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [report, setReport] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchServiceReport();
  }, [jobId]);

  const fetchServiceReport = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/jobs/${jobId}`);
      console.log('API Response:', response);

      // Handle both response formats: {job: {...}} or direct job object
      const jobData = response.job || response;
      console.log('Job data:', jobData);
      console.log('Service Report:', jobData.serviceReport);

      setJob(jobData);
      setReport(jobData.serviceReport);
    } catch (error) {
      console.error('Error fetching service report:', error);
      
      toast.error('Failed to load service report');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyReport = async () => {
    try {
      const updatedReport = {
        ...report,
        verified: true,
        verifiedAt: new Date()
      };

      await api.patch(`/jobs/${jobId}`, {
        serviceReport: updatedReport
      });

      toast.success('Service report verified successfully!');
      fetchServiceReport();
    } catch (error) {
      console.error('Error verifying service report:', error);
      toast.error('Failed to verify service report');
    }
  };

  const handleRejectReport = async () => {
    try {
      const updatedReport = {
        ...report,
        verified: false,
        verifiedAt: null
      };

      await api.patch(`/jobs/${jobId}`, {
        serviceReport: updatedReport
      });

      toast.success('Service report verification removed');
      fetchServiceReport();
    } catch (error) {
      console.error('Error rejecting service report:', error);
      toast.error('Failed to reject service report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading service report...</p>
        </div>
      </div>
    );
  }

  if (!job || !report || !report.submittedAt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Service Report Not Found</h2>
          <p className="text-gray-600 mb-2">
            {!job ? 'Job not found.' : 'This job doesn\'t have a service report yet.'}
          </p>
          {job && (
            <p className="text-sm text-gray-500 mb-6">
              Job #{job.jobNumber} - {job.status}
              <br />
              The vendor needs to submit a service report first.
            </p>
          )}
          <button
            onClick={() => navigate('/admin/orders')}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/orders')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <FileText className="w-7 h-7 text-orange-600" />
                  Service Report
                </h1>
                <p className="text-sm text-gray-600">Job #{job.jobNumber}</p>
              </div>
            </div>

            {/* Verification Status */}
            <div className="flex items-center gap-3">
              {report.verified ? (
                <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Verified</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Pending Verification</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Report Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Basic Information
                </h2>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Date of Attendance</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {report.dateOfAttendance
                      ? new Date(report.dateOfAttendance).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Work Order No.</p>
                  <p className="text-lg font-semibold text-gray-900">{report.workOrderNumber || job.jobNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Submitted At</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {new Date(report.submittedAt).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Payment Mode</p>
                  <p className="text-lg font-semibold text-gray-900">{report.paymentMode || 'N/A'}</p>
                </div>
              </div>
            </motion.div>

            {/* Customer Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-orange-400 to-orange-500 px-6 py-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Customer Details
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {report.customerDetails?.name || job.customerId?.name || 'N/A'}
                    </p>
                    {report.customerDetails?.mcstNumber && (
                      <p className="text-sm text-gray-500">MCST: {report.customerDetails.mcstNumber}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Telephone</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {report.customerDetails?.telephone || job.customerId?.phone || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {report.customerDetails?.email || job.customerId?.email || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {report.customerDetails?.address || job.location?.address || 'N/A'}
                      {report.customerDetails?.unitNumber && ` / ${report.customerDetails.unitNumber}`}
                    </p>
                    {report.customerDetails?.postalCode && (
                      <p className="text-sm text-gray-500">Postal Code: {report.customerDetails.postalCode}</p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Subscription Plan */}
            {report.subscriptionPlan && report.subscriptionPlan.type && report.subscriptionPlan.type !== 'NONE' && (


              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    Subscription Plan
                  </h2>
                </div>
                <div className="p-6">
                  <div className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-800 rounded-lg">
                    <span className="font-semibold text-lg">{report.subscriptionPlan.type}</span>
                    {report.subscriptionPlan.subtype && (
                      <span className="ml-2 text-orange-700">- {report.subscriptionPlan.subtype}</span>
                    )}
                  </div>
                  {report.subscriptionPlan.sqft && (
                    <p className="mt-3 text-gray-700">
                      <span className="font-medium">Size:</span> {report.subscriptionPlan.sqft} sqft
                    </p>
                  )}
                  {report.subscriptionPlan.rooms && (
                    <p className="mt-2 text-gray-700">
                      <span className="font-medium">Rooms:</span> {report.subscriptionPlan.rooms}
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Preventive Maintenance */}
            {report.preventiveMaintenance && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Preventive Maintenance
                  </h2>
                </div>
                <div className="p-6 space-y-6">
                  {/* Monthly Visual Check */}
                  {report.preventiveMaintenance.monthlyVisualCheck?.performed && (
                    <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        Monthly Visual & Functional Check
                      </h3>

                      {/* Electrical */}
                      {Object.values(report.preventiveMaintenance.monthlyVisualCheck.electrical || {}).some(v => v) && (
                        <div className="mb-4">
                          <h4 className="font-medium text-gray-800 mb-2">Electrical</h4>
                          <div className="ml-4 space-y-1.5">
                            {report.preventiveMaintenance.monthlyVisualCheck.electrical?.lightingPoints && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                All lighting points operational
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.electrical?.switches && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Light switches function correctly
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.electrical?.powerSockets && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Power sockets tested and working
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.electrical?.dbCoverSecure && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Distribution Board (DB) cover is secure
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.electrical?.noBurningSigns && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                No signs of burning/overheating
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.electrical?.rccbTested && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                RCCB/ELCB trip button tested
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Plumbing */}
                      {Object.values(report.preventiveMaintenance.monthlyVisualCheck.plumbing || {}).some(v => v) && (
                        <div className="mb-4">
                          <h4 className="font-medium text-gray-800 mb-2">Plumbing</h4>
                          <div className="ml-4 space-y-1.5">
                            {report.preventiveMaintenance.monthlyVisualCheck.plumbing?.exposedPipesLeaks && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Exposed pipes inspected for leaks
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.plumbing?.exposedPipesCorrosion && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Exposed pipes inspected for corrosion
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.plumbing?.tapsAndFaucets && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Taps and faucets checked for drips
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.plumbing?.toiletFlushing && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Toilet flushing system checked
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.plumbing?.bidetSprayer && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Bidet/sprayer checked for leaks
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.plumbing?.drainsClear && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Drains cleared
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Fixtures & Fittings */}
                      {Object.values(report.preventiveMaintenance.monthlyVisualCheck.fixturesAndFittings || {}).some(v => v) && (
                        <div>
                          <h4 className="font-medium text-gray-800 mb-2">Fixtures & Fittings</h4>
                          <div className="ml-4 space-y-1.5">
                            {report.preventiveMaintenance.monthlyVisualCheck.fixturesAndFittings?.doorHandles && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Door handles, hinges, and locks secure
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.fixturesAndFittings?.cabinetHinges && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Cabinet hinges and handles secure
                              </div>
                            )}
                            {report.preventiveMaintenance.monthlyVisualCheck.fixturesAndFittings?.windowLatches && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                Window latches and hinges secure
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Half-Yearly Service */}
                  {report.preventiveMaintenance.halfYearlyService?.performed && (
                    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-blue-600" />
                        Half-Yearly Service
                      </h3>
                      <div className="space-y-1.5">
                        {report.preventiveMaintenance.halfYearlyService.shortCircuitTest && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                            Short Circuit / Insulation Test performed
                            {report.preventiveMaintenance.halfYearlyService.nextScheduled && (
                              <span className="ml-2 text-xs text-gray-500">
                                (Next: {new Date(report.preventiveMaintenance.halfYearlyService.nextScheduled).toLocaleDateString()})
                              </span>
                            )}
                          </div>
                        )}
                        {report.preventiveMaintenance.halfYearlyService.acFilters && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                            Air-conditioner filters cleaned
                          </div>
                        )}
                        {report.preventiveMaintenance.halfYearlyService.kitchenHoodFilters && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                            Kitchen hood filters cleaned
                          </div>
                        )}
                        {report.preventiveMaintenance.halfYearlyService.smokeDetectors && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-blue-600 mr-2" />
                            Smoke/heat detectors tested
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Minor Home Fixes */}
                  {report.preventiveMaintenance.minorHomeFixes?.performed && (
                    <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-amber-600" />
                        Minor Home Fixes
                      </h3>
                      <div className="space-y-1.5">
                        {report.preventiveMaintenance.minorHomeFixes.tightenFittings && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />
                            Tightened loose fittings/fixtures
                          </div>
                        )}
                        {report.preventiveMaintenance.minorHomeFixes.replaceBulbs && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />
                            Replaced non-functioning light bulbs
                          </div>
                        )}
                        {report.preventiveMaintenance.minorHomeFixes.plumbingDrips && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />
                            Addressed minor plumbing drips
                          </div>
                        )}
                        {report.preventiveMaintenance.minorHomeFixes.drainClogs && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />
                            Cleared simple drain clogs
                          </div>
                        )}
                        {report.preventiveMaintenance.minorHomeFixes.adjustDoors && (
                          <div className="flex items-center text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-amber-600 mr-2" />
                            Adjusted sticking doors or cabinets
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {!report.preventiveMaintenance.monthlyVisualCheck?.performed &&
                   !report.preventiveMaintenance.halfYearlyService?.performed &&
                   !report.preventiveMaintenance.minorHomeFixes?.performed && (
                    <p className="text-center text-gray-500 italic py-4">
                      No preventive maintenance tasks performed
                    </p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Ad-Hoc Works */}
            {report.adHocWorks && (report.adHocWorks.description || report.adHocWorks.propertyConditionPhotos?.length > 0) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-orange-600 to-orange-700 px-6 py-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Ad-Hoc Works
                  </h2>
                </div>
                <div className="p-6 space-y-4">
                  {report.adHocWorks.description && (
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Description:</p>
                      <p className="text-gray-900 whitespace-pre-wrap">{report.adHocWorks.description}</p>
                    </div>
                  )}

                  {report.adHocWorks.propertyConditionPhotos && report.adHocWorks.propertyConditionPhotos.length > 0 && (
                    <div>
                      <p className="text-sm text-gray-600 mb-3">Property Condition Photos:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {report.adHocWorks.propertyConditionPhotos.map((photo, index) => (
                          <div
                            key={index}
                            className="relative group cursor-pointer"
                            onClick={() => setSelectedImage(photo)}
                          >
                            <img
                              src={photo}
                              alt={`Property condition ${index + 1}`}
                              className="w-full h-40 object-cover rounded-lg border border-gray-300 group-hover:border-orange-500 transition-colors"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                              <ImageIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Ad-hoc works are not included in the subscription fee and are subject to separate billing.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Customer Acknowledgement */}
            {report.customerAcknowledgement && report.customerAcknowledgement.signature && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Customer Acknowledgement
                  </h2>
                </div>
                <div className="p-6">
                  <img
                    src={report.customerAcknowledgement.signature}
                    alt="Customer Signature"
                    className="h-32 border border-gray-300 rounded bg-white px-4 py-2"
                  />
                  <p className="text-sm text-gray-600 mt-3">
                    Signed on: {report.customerAcknowledgement.date
                      ? new Date(report.customerAcknowledgement.date).toLocaleDateString()
                      : 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500 mt-2 italic">
                    I acknowledge completion of the described works, charges (if any), and receipt of this service report.
                  </p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Charges & Actions */}
          <div className="lg:col-span-1 space-y-6">
            {/* Charges */}
            {report.charges && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden sticky top-24"
              >
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Charges
                  </h2>
                </div>
                <div className="p-6 space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Ad-hoc works:</span>
                    <span className="font-semibold text-gray-900 text-lg">
                      SGD {report.charges.adHocWorksTotal?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-gray-600">Parts/Materials:</span>
                    <span className="font-semibold text-gray-900 text-lg">
                      SGD {report.charges.partsMaterialsCost?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="font-bold text-gray-900 text-lg">Total:</span>
                    <span className="font-bold text-orange-600 text-2xl">
                      SGD {report.charges.totalAmount?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>

                {/* Admin Actions */}
                <div className="p-6 bg-gray-50 border-t border-gray-200 space-y-3">
                  {report.verified ? (
                    <>
                      <div className="flex items-center gap-2 text-green-700 bg-green-100 px-4 py-3 rounded-lg">
                        <CheckCircle className="w-5 h-5" />
                        <div>
                          <p className="font-semibold">Verified</p>
                          {report.verifiedAt && (
                            <p className="text-xs">
                              {new Date(report.verifiedAt).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={handleRejectReport}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                      >
                        <XCircle className="w-5 h-5" />
                        Remove Verification
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleVerifyReport}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Verify Report
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/admin/orders`)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Back to Orders
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-6xl max-h-full">
            <img
              src={selectedImage}
              alt="Full size view"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-200 transition-colors"
            >
              <XCircle className="w-6 h-6 text-gray-900" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceReportManagement;
